/**
 * The printer portal reads (api-server printer-portal.serializers), mapped
 * into the screen's shapes through a whitelist. Defence in depth (frontend
 * spec 8): a field the server should not have sent (owed, source, PO,
 * another partner, internal notes) never reaches the cache or the screen.
 * The server's own redaction is the real gate.
 */
import { bySizeOrder, inSizeOrder } from "../../../lib/sizeOrder.ts";
import type { Onward, PartnerAddress, PrinterPortalLane } from "../../PartnerFulfillment/types.ts";
import type {
  PortalHoldView,
  PrinterPortalCard,
  PrinterPortalFile,
  PrinterPortalItem,
  PrinterPortalItemSummary,
  PrinterPortalLanePage,
  PrinterPortalOrder,
  PrinterPortalSummary,
} from "../types.ts";

type Raw = Record<string, unknown>;

const LANES: readonly PrinterPortalLane[] = [
  "blanks_in_transit",
  "blanks_received",
  "in_production",
  "shipping",
  "shipped",
  "delivered",
  "cancelled",
];

const obj = (value: unknown): Raw | null => (value && typeof value === "object" && !Array.isArray(value) ? (value as Raw) : null);
const str = (value: unknown): string => (typeof value === "string" ? value : value == null ? "" : String(value));
const strOrNull = (value: unknown): string | null => (value == null || value === "" ? null : String(value));
const num = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : Number(value) || 0);
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

function lane(value: unknown): PrinterPortalLane {
  if (value === "internal_transit") return "shipped";
  return LANES.includes(value as PrinterPortalLane) ? (value as PrinterPortalLane) : "blanks_in_transit";
}

/** API `destination.kind`: client · participants · next_stop (a printer or an FC; the portal never says which). */
function onward(value: unknown): Onward {
  if (value === "participants") return "participants";
  if (value === "next_stop") return "printer";
  return "client";
}

/** API address (`street1`, `zipCode`) → the screen's. */
export function mapAddress(value: unknown): PartnerAddress | null {
  const raw = obj(value);
  if (!raw) return null;
  const address = {
    name: strOrNull(raw.name),
    line1: str(raw.street1 ?? raw.line1),
    line2: strOrNull(raw.street2 ?? raw.line2),
    city: str(raw.city),
    state: str(raw.state),
    zip: str(raw.zipCode ?? raw.zip),
  };
  /* A name with no street, city, state or zip is no address: never an empty location row (G82). */
  return address.line1 || address.city || address.state || address.zip ? address : null;
}

function hold(value: unknown): PortalHoldView | null {
  const raw = obj(value);
  // G127: printers never see the staff hold reason or who.
  return raw ? { since: strOrNull(raw.at ?? raw.since) } : null;
}

/** API sizes `{ "M": 28 }` → `[{ size, qty }]`, in standard size order (G125). */
function sizes(value: unknown): { size: string; qty: number }[] {
  if (Array.isArray(value)) {
    return value.map((entry) => {
      const s = obj(entry) ?? {};
      return { size: str(s.size), qty: num(s.qty) };
    }).sort((a, b) => bySizeOrder(a.size, b.size));
  }
  const raw = obj(value) ?? {};
  return Object.entries(inSizeOrder(raw)).map(([size, qty]) => ({ size, qty: num(qty) }));
}

function itemSummary(value: unknown): PrinterPortalItemSummary {
  const raw = obj(value) ?? {};
  return {
    id: str(raw.orderItemId ?? raw.id),
    code: str(raw.code),
    name: str(raw.title ?? raw.name),
    /* API `style` ("Comfort Colors 6030") + `color` → "Comfort Colors 6030 · Pepper". */
    blank: [strOrNull(raw.style), strOrNull(raw.color ?? raw.blank)].filter(Boolean).join(" · "),
    imageUrl: strOrNull(raw.imageUrl),
    units: num(raw.units),
  };
}

function itemBlanks(value: unknown) {
  const raw = obj(value);
  return raw
    ? {
        eta: strOrNull(raw.arrivingOn ?? raw.eta),
        receivedOn: strOrNull(raw.receivedOn),
        trackingNumber: strOrNull(raw.trackingNumber),
        carrier: strOrNull(raw.carrier),
        late: raw.late === true,
      }
    : null;
}

function cardFields(raw: Raw): Omit<PrinterPortalCard, "items"> {
  const destination = obj(raw.destination) ?? {};
  const nextRaw = destination.kind === "next_stop" ? mapAddress(destination.address) : null;
  /* A next stop is only an address under "Habits365 Greek · Receiving": never the partner's name. */
  const next = nextRaw ? { ...nextRaw, name: null } : null;
  const items = list(raw.items);
  const lines = items.map((item) => itemBlanks(obj(item)?.blanks)).filter((line) => line !== null);
  const pending = lines.filter((line) => !line.receivedOn);
  const etas = pending.map((line) => line.eta).filter((eta): eta is string => Boolean(eta)).sort();
  const laneValue = lane(raw.lane);
  return {
    id: str(raw.id),
    label: str(raw.key ?? raw.label),
    stopId: str(raw.stopId),
    title: str(raw.orderTitle ?? raw.title),
    lane: laneValue,
    isCurrentStop: raw.isCurrentStop === true,
    canMove: raw.canMove === true,
    version: typeof raw.version === "number" ? raw.version : null,
    stop: { seq: Math.max(1, num(raw.stopSeq)), count: Math.max(1, num(raw.stopCount)) },
    onward: onward(destination.kind),
    shipCity: next ? [next.city, next.state].filter(Boolean).join(", ") : str(raw.shipCity),
    nextStopAddress: next,
    printTypes: [...new Set(items.flatMap((item) => list(obj(item)?.printTypes).map(str)))],
    totalUnits: num(raw.units ?? raw.totalUnits),
    blanks: lines.length > 0 ? { eta: etas.at(-1) ?? null, received: lines.length - pending.length, items: items.length } : null,
    blanksLate: raw.blanksLate === true,
    itemsArrive: strOrNull(raw.itemsArriveOn ?? raw.itemsArrive),
    tracking: { count: num(raw.trackingCount), unlabelledBoxes: num(raw.unlabelledBoxes), labels: null, scans: null },
    issueOpen: raw.issueOpen === true,
    hold: hold(raw.hold),
    cancelled: laneValue === "cancelled" ? { reason: str(obj(raw.cancelled)?.reason), at: strOrNull(obj(raw.cancelled)?.at) } : null,
    shipBy: strOrNull(raw.shipBy),
    deadline: strOrNull(raw.deadline),
    workOn: strOrNull(raw.workOn),
  };
}

export function redactCard(value: unknown): PrinterPortalCard {
  const raw = obj(value) ?? {};
  return { ...cardFields(raw), items: list(raw.items).map(itemSummary) };
}

export function redactLanePage(value: unknown): PrinterPortalLanePage {
  const raw = obj(value) ?? {};
  return { orders: list(raw.cards ?? raw.orders).map(redactCard), total: num(raw.total), nextCursor: strOrNull(raw.nextCursor) };
}

/** `/schedule` answers `{ entries, notPlanned }`; the calendar wants one list. */
export function redactSchedule(value: unknown): PrinterPortalCard[] {
  const raw = obj(value);
  const all = raw ? [...list(raw.entries), ...list(raw.notPlanned)] : list(value);
  const seen = new Set<string>();
  return all.map(redactCard).filter((card) => (seen.has(card.id) ? false : (seen.add(card.id), true)));
}

export function redactSummary(value: unknown): PrinterPortalSummary {
  const raw = obj(obj(value)?.kpis) ?? obj(value) ?? {};
  return {
    open: num(raw.open),
    dueThisWeek: num(raw.dueThisWeek),
    overdue: num(raw.overdue),
    blanksLate: num(raw.blanksLate),
    awaitingTracking: num(raw.awaitingTracking),
  };
}

function item(value: unknown): PrinterPortalItem {
  const raw = obj(value) ?? {};
  const blanks = itemBlanks(raw.blanks);
  return {
    ...itemSummary(raw),
    printDetails: list(raw.printDetails ?? raw.printTypes).map(str),
    sizes: sizes(raw.sizes),
    blanksLine: blanks
      ? { eta: blanks.eta, receivedOn: blanks.receivedOn, trackingNumber: blanks.trackingNumber, carrier: blanks.carrier }
      : null,
  };
}

export function redactOrder(value: unknown): PrinterPortalOrder {
  const raw = obj(value) ?? {};
  const note = raw.partnerNote;
  const fields = cardFields(raw);
  const shipTo = mapAddress(raw.shipTo);
  return {
    ...fields,
    clientName: str(raw.clientName),
    items: list(raw.items).map(item),
    shipTo: shipTo && fields.onward === "printer" ? { ...shipTo, name: null } : shipTo,
    notes: typeof note === "string" && note.trim() ? { text: note, at: strOrNull(raw.partnerNoteAt) } : null,
    participants: list(raw.participants)
      .map((entry) => {
        const p = obj(entry) ?? {};
        /* The API puts the participant's name on the address. */
        return { id: str(p.id), name: str(p.name ?? obj(p.address)?.name), address: mapAddress(p.address) };
      })
      /* Natural order: Participant 2 before Participant 10 (G82). */
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
  };
}

/** `/files` answers `{ items: [{ orderItemId, files: [{ id, fileName, mimeType, isPrimary }] }] }`. */
export function redactFiles(value: unknown): PrinterPortalFile[] {
  return list(obj(value)?.items).flatMap((group) => {
    const g = obj(group) ?? {};
    return list(g.files).map((file) => {
      const f = obj(file) ?? {};
      return {
        id: str(f.id),
        orderItemId: str(g.orderItemId),
        name: str(f.fileName ?? f.name),
        kind: f.isPrimary === true || /mockup/i.test(str(f.fileName)) ? "mockup" : "design",
        sizeBytes: typeof f.sizeBytes === "number" ? f.sizeBytes : null,
      } satisfies PrinterPortalFile;
    });
  });
}
